import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { UserIcon, LockIcon, CogIcon } from '../../components/ui/Icons';
import ProfilePanel from './ProfilePanel';
import PreferencesPanel from './PreferencesPanel';
import { moduleConfig } from './config';
import SidebarCustomizer from '../../components/Layout/SidebarCustomizer';
import { useOfficeSidebarItems } from '../../components/Layout/useOfficeSidebarItems';
import {
  fetchSidebarLayout,
  getCachedSidebarLayout,
  type SidebarLayout,
} from '../../utils/sidebarLayout';

export type AccountSection = 'profile' | 'security' | 'workspace' | 'menu';

const ACCOUNT_BASE = moduleConfig.module.route.path;

const MenuIcon: React.FC<{ className?: string }> = ({ className = 'h-4 w-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
  </svg>
);

const LEGACY_SECTIONS: Record<string, AccountSection> = {
  appearance: 'workspace',
  tables: 'workspace',
  display: 'workspace',
  navigation: 'workspace',
};

const SECTIONS: Array<{
  id: AccountSection;
  label: string;
  description: string;
  icon: React.ReactNode;
}> = [
  {
    id: 'profile',
    label: 'Profile',
    description: 'Name and email',
    icon: <UserIcon className="h-4 w-4" />,
  },
  {
    id: 'security',
    label: 'Security',
    description: 'Password',
    icon: <LockIcon className="h-4 w-4" />,
  },
  {
    id: 'workspace',
    label: 'Workspace',
    description: 'Theme, tables, bookmarks & navigation',
    icon: <CogIcon className="h-4 w-4" />,
  },
  {
    id: 'menu',
    label: 'Customize menu',
    description: 'Reorder groups and shortcuts',
    icon: <MenuIcon />,
  },
];

const VALID = new Set<string>(SECTIONS.map((s) => s.id));

function initialsFrom(first?: string, last?: string, email?: string): string {
  const a = (first || '').trim().charAt(0);
  const b = (last || '').trim().charAt(0);
  if (a || b) return `${a}${b}`.toUpperCase();
  return ((email || '').trim().charAt(0) || '?').toUpperCase();
}

export function resolveAccountSection(raw: string | null | undefined): AccountSection {
  if (raw && VALID.has(raw)) return raw as AccountSection;
  if (raw && LEGACY_SECTIONS[raw]) return LEGACY_SECTIONS[raw];
  return 'profile';
}

/**
 * Combined Profile + Preferences with an in-page settings sidebar.
 * Section is driven by `?section=` (defaults to profile).
 */
const AccountSettings: React.FC = () => {
  const { user, can, permissionsLoaded } = useAuth();
  const { colors } = useTheme();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const agencyMode = String(user?.typeLocation ?? '').toUpperCase() === 'MANNING_AGENT';
  const visibleSections = useMemo(
    () => (agencyMode ? SECTIONS.filter((s) => s.id !== 'profile') : SECTIONS),
    [agencyMode],
  );
  const defaultSection: AccountSection = agencyMode ? 'security' : 'profile';
  const requested = resolveAccountSection(params.get('section'));
  const section = visibleSections.some((s) => s.id === requested) ? requested : defaultSection;
  const canView = useMemo(
    () => (permissionsLoaded || user?.role === 'ADMIN' ? (moduleKey: string) => can(moduleKey, 'view') : undefined),
    [can, permissionsLoaded, user?.role],
  );
  const sidebarBaseItems = useOfficeSidebarItems(canView);
  const [sidebarLayout, setSidebarLayout] = useState<SidebarLayout | null>(() => getCachedSidebarLayout());

  const setSection = (next: AccountSection) => {
    navigate(`${ACCOUNT_BASE}?section=${next}`, { replace: true });
  };

  useEffect(() => {
    let cancelled = false;
    fetchSidebarLayout().then((layout) => {
      if (!cancelled) setSidebarLayout(layout);
    });
    const onUpdated = () => {
      setSidebarLayout(getCachedSidebarLayout());
      fetchSidebarLayout().then((layout) => {
        if (!cancelled) setSidebarLayout(layout);
      });
    };
    window.addEventListener('sidebarLayoutUpdated', onUpdated);
    return () => {
      cancelled = true;
      window.removeEventListener('sidebarLayoutUpdated', onUpdated);
    };
  }, []);

  useEffect(() => {
    const raw = params.get('section');
    if (agencyMode && (raw === 'profile' || raw == null || raw === '')) {
      navigate(`${ACCOUNT_BASE}?section=${defaultSection}`, { replace: true });
      return;
    }
    const resolved = resolveAccountSection(raw);
    const allowed = visibleSections.some((s) => s.id === resolved) ? resolved : defaultSection;
    if (raw !== allowed) {
      navigate(`${ACCOUNT_BASE}?section=${allowed}`, { replace: true });
    }
  }, [params, navigate, agencyMode, defaultSection, visibleSections]);

  const displayName = useMemo(() => {
    const custom = user?.userDisplayName?.trim();
    if (custom) return custom;
    const full = `${user?.userFirstName || ''} ${user?.userLastName || ''}`.trim();
    return full || user?.userEmail || 'Your account';
  }, [user]);

  const initials = useMemo(
    () => initialsFrom(user?.userFirstName, user?.userLastName, user?.userEmail),
    [user],
  );

  const activeMeta = visibleSections.find((s) => s.id === section) ?? visibleSections[0]!;

  return (
    <div className="h-full overflow-hidden">
      <div className="mx-auto flex h-full max-w-6xl flex-col gap-0 lg:flex-row">
        {/* Settings sidebar */}
        <aside
          className={cn(
            'shrink-0 border-b lg:w-64 lg:border-b-0 lg:border-r',
            colors.border,
            colors.surface,
          )}
        >
          <div className="flex h-full flex-col">
            <div className={cn('border-b px-4 py-4 sm:px-5', colors.border)}>
              <div className="flex items-center gap-3">
                <div
                  className={cn(
                    'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-sm font-semibold tracking-wide text-white',
                    'bg-gradient-to-br from-[#0a1424] via-[#143052] to-[#3d5a80]',
                  )}
                  aria-hidden
                >
                  {initials}
                </div>
                <div className="min-w-0">
                  <p className={cn('truncate text-sm font-semibold', colors.text)}>{displayName}</p>
                  <p className={cn('truncate text-xs', colors.textMuted)}>
                    {user?.userEmail || 'Account settings'}
                  </p>
                </div>
              </div>
            </div>

            <nav className="flex-1 space-y-5 overflow-y-auto p-3 sm:p-4" aria-label="Account settings">
              {/* Mobile: horizontal chips */}
              <div className="flex gap-1.5 overflow-x-auto pb-1 lg:hidden">
                {visibleSections.map((item) => {
                  const active = item.id === section;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setSection(item.id)}
                      className={cn(
                        'inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors',
                        active
                          ? cn(colors.primary, colors.primaryText)
                          : cn(colors.surfaceHover, colors.textSecondary, 'ring-1', colors.border),
                      )}
                    >
                      {item.icon}
                      {item.label}
                    </button>
                  );
                })}
              </div>

              {/* Desktop sidebar */}
              <ul className="hidden space-y-0.5 lg:block">
                {visibleSections.map((item) => {
                  const active = item.id === section;
                  return (
                    <li key={item.id}>
                      <button
                        type="button"
                        onClick={() => setSection(item.id)}
                        aria-current={active ? 'page' : undefined}
                        className={cn(
                          'flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left transition-colors',
                          active
                            ? cn(colors.surfaceHover, 'ring-1', colors.border)
                            : colors.interactiveHover,
                        )}
                      >
                        <span
                          className={cn(
                            'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
                            active
                              ? cn(colors.primary, colors.primaryText)
                              : cn(
                                  'bg-slate-100 text-slate-500 dark:bg-white/5 dark:text-slate-400',
                                ),
                          )}
                        >
                          {item.icon}
                        </span>
                        <span className="min-w-0">
                          <span className={cn('block text-sm font-medium', colors.text)}>
                            {item.label}
                          </span>
                          <span className={cn('block text-[11px]', colors.textMuted)}>
                            {item.description}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </div>
        </aside>

        {/* Content */}
        <main className="min-w-0 flex-1 overflow-y-auto">
          <div className="px-4 py-5 sm:px-6 sm:py-6 lg:px-8">
            <div className="mb-5">
              <h1 className={cn('text-lg font-semibold tracking-tight sm:text-xl', colors.text)}>
                {activeMeta.label}
              </h1>
              <p className={cn('mt-0.5 text-sm', colors.textSecondary)}>{activeMeta.description}</p>
            </div>

            {section === 'profile' && !agencyMode && (
              <ProfilePanel embedded initialSection="profile" hideSectionSwitcher />
            )}

            {section === 'security' && (
              <ProfilePanel embedded initialSection="password" hideSectionSwitcher />
            )}

            {section === 'workspace' && <PreferencesPanel embedded section="all" />}

            {section === 'menu' && (
              <SidebarCustomizer
                embedded
                baseItems={sidebarBaseItems}
                initialLayout={sidebarLayout}
              />
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AccountSettings;
